package com.example.positionahead.model

import kotlinx.serialization.Serializable

@Serializable
enum class PortfolioObjective {
    GROWTH,
    INCOME,
    BALANCED,
    CAPITAL_PRESERVATION,
}

@Serializable
enum class PlannerGoalType {
    RETIREMENT,
    WEALTH_BUILDING,
    HOME_PURCHASE,
    EDUCATION,
    EMERGENCY_FUND,
    FINANCIAL_INDEPENDENCE,
    CUSTOM,
}

@Serializable
data class FinancialGoal(
    val plannerGoalType: PlannerGoalType = PlannerGoalType.WEALTH_BUILDING,
    val customGoalLabel: String? = null,
    val targetAmount: Double = 1_000_000.0,
    val targetDateYears: Int = 20,
    val monthlyContribution: Double = 500.0,
    val age: Int = 35,
    val riskTolerance: RiskTolerance = RiskTolerance.MODERATE,
    val objective: PortfolioObjective = PortfolioObjective.BALANCED,
    val targetStockPercent: Int? = null,
    val maxSinglePositionPercent: Double = 10.0,
    val maxSectorPercent: Double = 25.0,
    val rebalanceThresholdPercent: Double = 5.0,
    val targetYieldPercent: Double = 2.5,
    val investmentHorizonYears: Int = 20,
)

enum class RiskTolerance {
    CONSERVATIVE,
    MODERATE,
    AGGRESSIVE,
}

fun FinancialGoal.effectiveStockTarget(): Int {
    targetStockPercent?.let { return it.coerceIn(0, 100) }
    val objectiveAdjusted = when (objective) {
        PortfolioObjective.GROWTH -> 10
        PortfolioObjective.INCOME -> -5
        PortfolioObjective.BALANCED -> 0
        PortfolioObjective.CAPITAL_PRESERVATION -> -15
    }
    val base = when (riskTolerance) {
        RiskTolerance.CONSERVATIVE -> 100 - age
        RiskTolerance.MODERATE -> 110 - age
        RiskTolerance.AGGRESSIVE -> 120 - age
    } + objectiveAdjusted
    return base.coerceIn(15, 95)
}

fun FinancialGoal.effectiveBondTarget(): Int = 100 - effectiveStockTarget()

fun FinancialGoal.objectiveLabel(): String = when (objective) {
    PortfolioObjective.GROWTH -> "Growth"
    PortfolioObjective.INCOME -> "Income"
    PortfolioObjective.BALANCED -> "Balanced"
    PortfolioObjective.CAPITAL_PRESERVATION -> "Capital preservation"
}

fun FinancialGoal.plannerGoalLabel(): String = when (plannerGoalType) {
    PlannerGoalType.CUSTOM -> customGoalLabel?.takeIf { it.isNotBlank() } ?: "Custom goal"
    PlannerGoalType.RETIREMENT -> "Retirement"
    PlannerGoalType.WEALTH_BUILDING -> "Wealth building"
    PlannerGoalType.HOME_PURCHASE -> "Home purchase"
    PlannerGoalType.EDUCATION -> "Education"
    PlannerGoalType.EMERGENCY_FUND -> "Emergency fund"
    PlannerGoalType.FINANCIAL_INDEPENDENCE -> "Financial independence"
}

fun FinancialGoal.progressPercent(currentValue: Double): Double {
    if (targetAmount <= 0) return 0.0
    return (currentValue / targetAmount * 100.0).coerceIn(0.0, 100.0)
}
